import { FC, useState } from 'react';

import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import usePricing from 'api/usePricing';
import { AxiosError } from 'axios';
import { EPermissionAction, EPermissionGroup } from 'common/permissions.types';
import { ERouteContext } from 'routes/types';
import { errorResponse } from 'utils/errorsTextHelper';
import { permissionsUtil } from 'utils/permissionsUtil';

import PricingModals from 'components/Pricing/PricingModals';

import Header from './Components/Header';
import PricingTabs, { EPricingTabs } from './Components/Tabs';

export enum EPricingFormState {
  PRICING_POINTS = 'points',
  SETTINGS = 'settings'
}

export const PricingView: FC<{ context: ERouteContext }> = ({ context }) => {
  const navigate = useNavigate();
  const { publisherId } = useParams();
  const location = useLocation();
  const { getPricingPointsTemplate, getPricings } = usePricing(publisherId);

  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPricing, setSelectedPricing] = useState<string | null>(null);
  const [isUploadCSVOpen, setUploadCSVOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const currentTab = location.pathname.split('/')[4] as EPricingTabs;

  const isPricingManage = permissionsUtil.isActionEnabled(
    null,
    EPermissionGroup.PRICING,
    EPermissionAction.MANAGE
  );

  const isCanUserEdit = permissionsUtil.canUserEdit();
  const isManageDisabled = !isPricingManage || !isCanUserEdit;

  const hasData = getPricings.data && getPricings.data.result.length > 0;
  const isExportDisabled = !hasData;

  const handleTabChange = (newValue: EPricingTabs) => {
    const newPath = `/${context === ERouteContext.PORTAL_LEVEL_STORE ? 'store' : 'project'}/${publisherId}/pricing/${newValue}`;
    navigate(newPath);
  };

  const handleAddPricePoint = () => {
    navigate('./form');
  };

  const handleImport = () => {
    setUploadCSVOpen(true);
  };

  const handleExport = () => {
    getPricingPointsTemplate
      .refetch()
      .then((response) => {
        const { data } = response;
        if (data) {
          const url = window.URL.createObjectURL(
            new Blob([data], { type: 'text/csv' })
          );
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'Import_price_point_template.csv');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
      })
      .catch((error) => {
        if (error instanceof AxiosError) {
          setErrorMessage(errorResponse(error));
        }
      });
  };

  return (
    <div className="w-full flex flex-col px-7">
      <Header
        onAddPricePoint={handleAddPricePoint}
        onImport={handleImport}
        onExport={handleExport}
        isExportDisabled={isExportDisabled}
        isManageDisabled={isManageDisabled}
      />
      <PricingTabs
        activeTab={currentTab}
        onTabChange={handleTabChange}
        showSettings={isPricingManage}
      />
      <div className="flex-1 overflow-auto">
        <Outlet
          context={{
            isDeleteDialogOpen,
            setDeleteDialogOpen,
            selectedPricing,
            setSelectedPricing,
            onAddPricePoint: handleAddPricePoint
          }}
        />
      </div>
      <PricingModals
        publisherId={publisherId}
        isUploadCSVOpen={isUploadCSVOpen}
        selectedPricing={selectedPricing}
        isDeleteDialogOpen={isDeleteDialogOpen}
        setSelectedPricing={setSelectedPricing}
        setUploadCSVOpen={setUploadCSVOpen}
        setDeleteDialogOpen={setDeleteDialogOpen}
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
      />
    </div>
  );
};
